import React, { useRef } from 'react'
import { ScrollView, StyleSheet, Text, View } from 'react-native'
import moment from 'moment'

import styles, { colors } from '../styles/global'
import { translate } from '../../i18n/TranslationService'
import store from '../../redux/store'
import { getDateFormat } from '../UIComponents/FloatModals/DateFormatPickerModal'
import {
    getGlobalHappinessStats,
    getHappinessDateText,
    getHappinessRatingText,
    getHappinessStats,
    HAPPINESS_EMOJIS,
    HAPPINESS_SCALE,
} from '../../utils/ProjectHappinessHelper'

const formatAverage = average => (average ? average.toFixed(1) : '0.0')

function SummaryItem({ label, value }) {
    return (
        <View style={localStyles.summaryItem}>
            <Text style={localStyles.summaryLabel}>{translate(label)}</Text>
            <Text style={localStyles.summaryValue}>{value}</Text>
        </View>
    )
}

function Distribution({ distribution, total }) {
    return (
        <View style={localStyles.section}>
            <Text style={localStyles.sectionTitle}>{translate('Happiness distribution')}</Text>
            {HAPPINESS_SCALE.map(rating => {
                const amount = distribution[rating] || 0
                const width = total ? `${Math.max((amount / total) * 100, amount ? 8 : 0)}%` : '0%'
                return (
                    <View key={rating} style={localStyles.distributionRow}>
                        <Text style={localStyles.distributionLabel}>{HAPPINESS_EMOJIS[rating]}</Text>
                        <View style={localStyles.distributionTrack}>
                            <View style={[localStyles.distributionBar, { width }]} />
                        </View>
                        <Text style={localStyles.distributionAmount}>{amount}</Text>
                    </View>
                )
            })}
        </View>
    )
}

const getGlobalTrendEntries = happinessByProject => {
    const entriesByDate = {}

    Object.values(happinessByProject).forEach(entries => {
        entries.forEach(entry => {
            if (!entry.rating) return

            const dateKey = entry.dateKey || `${entry.timestamp}`
            if (!entriesByDate[dateKey]) {
                entriesByDate[dateKey] = { timestamp: entry.timestamp, ratings: [] }
            }
            entriesByDate[dateKey].ratings.push(entry.rating)
        })
    })

    return Object.entries(entriesByDate).map(([dateKey, data]) => {
        const rating = data.ratings.reduce((sum, value) => sum + value, 0) / data.ratings.length

        return {
            dateKey,
            timestamp: data.timestamp,
            rating,
        }
    })
}

/** The day a trend bar stands for, short (no year): `02/09` or `09/02`. */
export const getHappinessTrendDateText = timestamp => moment(timestamp).format(getDateFormat(true))

/**
 * The last 14 rated days as bars, each labelled with its rating AND its date.
 *
 * Without the date a bar is just a shape: the strip can hold a fortnight of
 * consecutive days or fourteen ratings scattered over a year, and the two
 * read identically. The strip scrolls horizontally and starts at its end, so
 * a phone shows the most recent days first rather than clipping them.
 */
function Trend({ entries, showAverageLabel }) {
    const scrollRef = useRef(null)
    const trendEntries = entries
        .filter(entry => entry.rating)
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(-14)

    return (
        <View style={localStyles.section}>
            <Text style={localStyles.sectionTitle}>{translate('Happiness trend')}</Text>
            {trendEntries.length > 0 ? (
                <ScrollView
                    ref={scrollRef}
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={localStyles.trendScroll}
                    contentContainerStyle={localStyles.trend}
                    onContentSizeChange={() => scrollRef.current?.scrollToEnd?.({ animated: false })}
                >
                    {trendEntries.map(entry => (
                        <View key={`${entry.projectId || ''}${entry.dateKey}`} style={localStyles.trendItem}>
                            <View style={localStyles.trendBarTrack}>
                                <View style={[localStyles.trendBar, { height: `${(entry.rating / 5) * 100}%` }]} />
                            </View>
                            <Text style={localStyles.trendEmoji}>
                                {showAverageLabel ? entry.rating.toFixed(1) : HAPPINESS_EMOJIS[entry.rating]}
                            </Text>
                            <Text
                                style={localStyles.trendDate}
                                numberOfLines={1}
                                testID={`happinessTrendDate_${entry.dateKey}`}
                            >
                                {getHappinessTrendDateText(entry.timestamp)}
                            </Text>
                        </View>
                    ))}
                </ScrollView>
            ) : (
                <Text style={localStyles.emptyText}>{translate('No happiness data yet')}</Text>
            )}
        </View>
    )
}

function RecentComments({ entries, isGlobal }) {
    const projectsById = store.getState().loggedUserProjects.reduce((acc, project) => {
        acc[project.id] = project
        return acc
    }, {})
    const comments = entries
        .filter(entry => entry.rating && entry.comment)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 10)

    if (comments.length === 0) return null

    return (
        <View style={localStyles.section}>
            <Text style={localStyles.sectionTitle}>{translate('Recent happiness notes')}</Text>
            {comments.map(entry => (
                <View key={`comment-${entry.projectId || ''}${entry.dateKey}`} style={localStyles.commentRow}>
                    <Text style={localStyles.commentMeta}>
                        {isGlobal && projectsById[entry.projectId] ? `${projectsById[entry.projectId].name} • ` : ''}
                        {getHappinessDateText(entry.timestamp)} · {getHappinessRatingText(entry.rating)}
                    </Text>
                    <Text style={localStyles.commentText}>{entry.comment}</Text>
                </View>
            ))}
        </View>
    )
}

export default function HappinessStatsPanel({
    entries = [],
    happinessByProject = null,
    showRecentComments = true,
    showTitle = true,
}) {
    const isGlobal = !!happinessByProject
    const globalStats = isGlobal ? getGlobalHappinessStats(happinessByProject) : null
    const stats = isGlobal ? globalStats : getHappinessStats(entries)
    const trendEntries = isGlobal ? getGlobalTrendEntries(happinessByProject) : entries
    const commentEntries = isGlobal ? Object.values(happinessByProject).flat() : entries
    const latest = isGlobal
        ? commentEntries.filter(entry => entry.rating).sort((a, b) => b.timestamp - a.timestamp)[0]
        : stats.latest

    return (
        <View style={localStyles.container}>
            {showTitle && <Text style={localStyles.title}>{translate('Happiness')}</Text>}
            <View style={localStyles.summary}>
                <SummaryItem label="Average" value={`${formatAverage(stats.average)}/5`} />
                <SummaryItem label="Latest" value={latest ? getHappinessRatingText(latest.rating) : '-'} />
                <SummaryItem label="Tracked days" value={stats.trackedDays} />
                {isGlobal && <SummaryItem label="Projects" value={stats.projectsTracked} />}
            </View>
            {latest && (
                <Text style={localStyles.latestText}>
                    {translate('Latest rating')}: {getHappinessDateText(latest.timestamp)}
                </Text>
            )}
            <Distribution distribution={stats.distribution} total={stats.trackedDays} />
            <Trend entries={trendEntries} showAverageLabel={isGlobal} />
            {showRecentComments && <RecentComments entries={commentEntries} isGlobal={isGlobal} />}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        marginTop: 24,
        marginBottom: 24,
    },
    title: {
        ...styles.title6,
        color: colors.Text01,
        marginBottom: 12,
    },
    summary: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginHorizontal: -4,
    },
    summaryItem: {
        minWidth: 140,
        flex: 1,
        borderWidth: 1,
        borderColor: colors.Grey200,
        borderRadius: 4,
        padding: 12,
        margin: 4,
    },
    summaryLabel: {
        ...styles.body2,
        color: colors.Text03,
    },
    summaryValue: {
        ...styles.title6,
        color: colors.Text01,
        marginTop: 4,
    },
    latestText: {
        ...styles.body2,
        color: colors.Text03,
        marginTop: 8,
    },
    section: {
        marginTop: 16,
    },
    sectionTitle: {
        ...styles.subtitle2,
        color: colors.Text02,
        marginBottom: 8,
    },
    distributionRow: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 28,
    },
    distributionLabel: {
        width: 32,
        ...styles.body1,
    },
    distributionTrack: {
        flex: 1,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.Grey200,
        overflow: 'hidden',
    },
    distributionBar: {
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.Primary300,
    },
    distributionAmount: {
        ...styles.body2,
        color: colors.Text03,
        width: 32,
        textAlign: 'right',
    },
    trendScroll: {
        borderBottomWidth: 1,
        borderColor: colors.Grey200,
    },
    trend: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        paddingBottom: 4,
    },
    trendItem: {
        width: 44,
        alignItems: 'center',
        marginRight: 6,
    },
    trendBarTrack: {
        width: 16,
        height: 88,
        justifyContent: 'flex-end',
        backgroundColor: colors.Grey200,
        borderRadius: 4,
        overflow: 'hidden',
    },
    trendBar: {
        width: 16,
        backgroundColor: colors.Primary300,
    },
    trendEmoji: {
        ...styles.caption2,
        marginTop: 4,
    },
    trendDate: {
        ...styles.caption2,
        lineHeight: 14,
        color: colors.Text03,
    },
    emptyText: {
        ...styles.body2,
        color: colors.Text03,
    },
    commentRow: {
        borderTopWidth: 1,
        borderColor: colors.Grey200,
        paddingVertical: 8,
    },
    commentMeta: {
        ...styles.body2,
        color: colors.Text03,
        marginBottom: 4,
    },
    commentText: {
        ...styles.body1,
        color: colors.Text01,
    },
})
